'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export interface LayoutContextValue {
  // Sidebar state
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Mobile menu state
  isMobileMenuOpen: boolean;
  toggleMobileMenu: () => void;
  setMobileMenuOpen: (open: boolean) => void;

  // Page loading state
  isPageLoading: boolean;
  setPageLoading: (loading: boolean) => void;

  // Breadcrumb management
  breadcrumbs: LayoutBreadcrumbItem[];
  setBreadcrumbs: (breadcrumbs: LayoutBreadcrumbItem[]) => void;

  // Active navigation item
  activeNavItem: string | null;
  setActiveNavItem: (itemId: string | null) => void;

  // Layout preferences
  layoutPreferences: LayoutPreferences;
  updateLayoutPreferences: (preferences: Partial<LayoutPreferences>) => void;
}

export interface LayoutBreadcrumbItem {
  id: string;
  label: string;
  href?: string;
  isCurrentPage?: boolean;
}

export interface LayoutPreferences {
  compactMode: boolean;
  showNotifications: boolean;
  sidebarPosition: 'left' | 'right';
  colorMode: 'light' | 'dark' | 'system';
  language: string;
}

const defaultLayoutPreferences: LayoutPreferences = {
  compactMode: false,
  showNotifications: true,
  sidebarPosition: 'left',
  colorMode: 'system',
  language: 'tr',
};

const LayoutContext = createContext<LayoutContextValue | undefined>(undefined);

export interface LayoutProviderProps {
  children: React.ReactNode;
  defaultSidebarCollapsed?: boolean;
  defaultPreferences?: Partial<LayoutPreferences>;
  onPreferencesChange?: (preferences: LayoutPreferences) => void;
}

const LAYOUT_STORAGE_KEY = 'tms-layout-preferences';

export function LayoutProvider({
  children,
  defaultSidebarCollapsed = false,
  defaultPreferences = {},
  onPreferencesChange,
}: LayoutProviderProps) {
  // Sidebar state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(defaultSidebarCollapsed);

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Page loading state
  const [isPageLoading, setIsPageLoading] = useState(false);

  // Breadcrumbs
  const [breadcrumbs, setBreadcrumbs] = useState<LayoutBreadcrumbItem[]>([]);

  // Active navigation
  const [activeNavItem, setActiveNavItem] = useState<string | null>(null);

  // Layout preferences
  const [layoutPreferences, setLayoutPreferences] = useState<LayoutPreferences>(() => {
    // Try to load from localStorage
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(LAYOUT_STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          return { ...defaultLayoutPreferences, ...defaultPreferences, ...parsed };
        } catch (e) {
          console.error('Failed to parse layout preferences:', e);
        }
      }
    }
    return { ...defaultLayoutPreferences, ...defaultPreferences };
  });

  // Toggle sidebar
  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => !prev);
  }, []);

  // Set sidebar collapsed state
  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
  }, []);

  // Toggle mobile menu
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  // Set mobile menu open state
  const setMobileMenuOpen = useCallback((open: boolean) => {
    setIsMobileMenuOpen(open);
  }, []);

  // Set page loading
  const setPageLoading = useCallback((loading: boolean) => {
    setIsPageLoading(loading);
  }, []);

  // Update layout preferences
  const updateLayoutPreferences = useCallback(
    (preferences: Partial<LayoutPreferences>) => {
      setLayoutPreferences((prev) => {
        const updated = { ...prev, ...preferences };

        // Save to localStorage
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(updated));
          } catch (e) {
            console.error('Failed to save layout preferences:', e);
          }
        }

        // Notify parent
        onPreferencesChange?.(updated);

        return updated;
      });
    },
    [onPreferencesChange]
  );

  // Close mobile menu on desktop resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen, setMobileMenuOpen]);

  // Apply color mode preference
  useEffect(() => {
    if (layoutPreferences.colorMode === 'system') {
      // Let the system preference take over
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', layoutPreferences.colorMode);
    }
  }, [layoutPreferences.colorMode]);

  const value: LayoutContextValue = {
    isSidebarCollapsed,
    toggleSidebar,
    setSidebarCollapsed,
    isMobileMenuOpen,
    toggleMobileMenu,
    setMobileMenuOpen,
    isPageLoading,
    setPageLoading,
    breadcrumbs,
    setBreadcrumbs,
    activeNavItem,
    setActiveNavItem,
    layoutPreferences,
    updateLayoutPreferences,
  };

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

export function useLayout() {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
}

// Convenience hooks for specific layout features
export function useSidebar() {
  const { isSidebarCollapsed, toggleSidebar, setSidebarCollapsed } = useLayout();
  return {
    isCollapsed: isSidebarCollapsed,
    toggle: toggleSidebar,
    setCollapsed: setSidebarCollapsed,
  };
}

export function useMobileMenu() {
  const { isMobileMenuOpen, toggleMobileMenu, setMobileMenuOpen } = useLayout();
  return { isOpen: isMobileMenuOpen, toggle: toggleMobileMenu, setOpen: setMobileMenuOpen };
}

export function usePageLoading() {
  const { isPageLoading, setPageLoading } = useLayout();
  return { isLoading: isPageLoading, setLoading: setPageLoading };
}

export function useBreadcrumbs() {
  const { breadcrumbs, setBreadcrumbs } = useLayout();
  return { breadcrumbs, setBreadcrumbs };
}

export function useLayoutPreferences() {
  const { layoutPreferences, updateLayoutPreferences } = useLayout();
  return { preferences: layoutPreferences, updatePreferences: updateLayoutPreferences };
}
